import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import type { PendingActionItem } from '../../api/pendingActions';
import { sourceMeta, relativeTime, leaveSubtitle, localizedTitle, localizedSubtitle } from './sourceMeta';
import { completeActionLabel, isDispatchable, offersNegativeDecision } from './dispatch';

export type RowDecision = 'positive' | 'negative' | 'open' | `rate-${number}` | null;

/** The one-tap eNPS answers offered inline on the Survey row. */
const SURVEY_QUICK_RATINGS = [0, 5, 10] as const;

interface PendingActionRowProps {
  item: PendingActionItem;
  locale: string;
  /** The in-flight decision on THIS row (disables its buttons). */
  busy: RowDecision;
  onDecision: (item: PendingActionItem, positive: boolean) => void;
  onOpen: (item: PendingActionItem) => void;
  onDetails: (item: PendingActionItem) => void;
  onQuickRate: (item: PendingActionItem, rating: number) => void;
  /** True when Details navigates to the source's page; false = opens the fallback dialog. */
  detailsNavigates?: boolean;
  /**
   * Home dashboard widget: opening the request ("Review") is the emphasised action and the inline
   * decisions are demoted to quiet neutral buttons. A one-click solid Approve on the first screen
   * read as weak control to an Internal Control audience. The full /pending-actions inbox keeps
   * its regular styling.
   */
  calm?: boolean;
}

/**
 * One inbox row: source icon, what's pending, who it's from, and the quick
 * action(s) inline. "Details" is always available; it is never the only path.
 */
const PendingActionRow: React.FC<PendingActionRowProps> = ({ item, locale, busy, onDecision, onOpen, onDetails, onQuickRate, detailsNavigates = false, calm = false }) => {
  const { t } = useTranslation(['requests', 'leaves']);
  const meta = sourceMeta(item.source);
  const SourceIcon = meta.icon;
  const disabled = busy !== null;

  // CO-346 / CO-675 — a leave inbox LISTS every request this principal may eventually decide, which is
  // wider than the ones they may decide NOW: a multi-step workflow parked on someone else's step (HR's
  // step after the manager approved step 1, or the manager's step while HR looks on) is refused by the
  // server. `canDecideNow` is the engine's own verdict (PendingActionsService.MapLeave). Mobile and the
  // /leave-approvals queue already grey these rows and name who the request waits on; the web inbox
  // painted a live Approve/Reject that could only fail. Only an explicit 'false' withdraws them.
  const notMyTurn = item.source === 'Leave' && item.meta?.canDecideNow === 'false';
  const notMyTurnReason = item.meta?.pendingApproverName
    ? t('leaves:awaitingNamedApprover', 'Waiting on {{name}} to approve this step', { name: item.meta.pendingApproverName })
    : t('leaves:notCurrentApprover', 'You are not the approver for the current step of this request');
  const notMyTurnId = `pa-not-my-turn-${item.source}-${item.id}`;
  const notMyTurnProps = notMyTurn ? { title: notMyTurnReason, 'aria-describedby': notMyTurnId } : {};

  // System-generated title/subtitle localize on the client from the item's
  // structured Meta; genuine user data (leave TYPE, requester name) stays as the
  // server sent it. The Survey subtitle is the fixed eNPS question.
  const title = localizedTitle(item, t);
  // CO-816 — Leave and Survey keep their own richer client-side builders (a formatted date range;
  // the full eNPS question, which is more useful than the generic server nudge). Every other source
  // whose subtitle the SERVER composes carries `subtitleKey`/`subtitleArgs` (`localizedSubtitle`,
  // shared with mobile), including Overtime, ShortLeave, Encashment, AssetRequest and
  // AttendanceCorrection since the CO-816 review. Subtitles that are user DATA (a claim amount and
  // type, a goal blocker's text, a prompt title, a profile slug, a department name) have no key on
  // purpose and render as sent.
  const subtitle =
    item.source === 'Survey'
      ? t('How likely are you to recommend this company as a great place to work?')
      : item.source === 'Leave'
        ? leaveSubtitle(item.meta, locale, t) ?? item.subtitle
        : localizedSubtitle(item, t);

  const decisionStyle = isDispatchable(item.source) ? item.actionStyle : 'OpenOnly';
  // Calm (dashboard) rows with an inline decision: Review leads, the decision buttons step back.
  const emphasiseReview = calm && item.source !== 'Survey' && decisionStyle !== 'OpenOnly';
  const negativeVariant = emphasiseReview ? 'ghost' : 'outline';
  const positiveVariant = emphasiseReview ? 'outline' : 'success';

  const actions: React.ReactNode = (() => {
    // Survey answers inline: the monthly pulse is a single 0–10 question, so
    // three anchor scores beat a detour through the survey page. The full
    // page (with feedback text) stays reachable via Details.
    if (item.source === 'Survey') {
      return (
        <div className="flex items-center gap-1" role="group" aria-label={t('Rate from 0 to 10')}>
          {SURVEY_QUICK_RATINGS.map((rating) => (
            <Button
              key={rating}
              variant="outline"
              size="xs"
              className="min-w-9 tabular-nums"
              disabled={disabled}
              loading={busy === `rate-${rating}`}
              onClick={() => onQuickRate(item, rating)}
              aria-label={t('Rate {{n}} out of 10', { n: rating })}
              data-testid={`pa-survey-rate-${rating}`}
            >
              {rating}
            </Button>
          ))}
        </div>
      );
    }
    // A decision button is only honest when `dispatchDecision` can carry it. The
    // server pairs each source with an actionStyle, but that pairing is across a
    // wire boundary: fall back to Open rather than paint an Approve that can only
    // return NotDispatchable.
    const style = decisionStyle;
    switch (style) {
      case 'Decision2':
        return (
          <>
            <Button
              variant={negativeVariant}
              size="xs"
              disabled={disabled || notMyTurn}
              loading={busy === 'negative'}
              onClick={() => onDecision(item, false)}
              aria-label={`${t('Reject')}: ${title}`}
              {...notMyTurnProps}
              data-testid="pa-reject"
            >
              {t('Reject')}
            </Button>
            <Button
              variant={positiveVariant}
              size="xs"
              disabled={disabled || notMyTurn}
              loading={busy === 'positive'}
              onClick={() => onDecision(item, true)}
              aria-label={`${t('Approve')}: ${title}`}
              {...notMyTurnProps}
              data-testid="pa-approve"
            >
              {t('Approve')}
            </Button>
          </>
        );
      case 'Accept2':
        return (
          <>
            {/* CO-780 — the Decline half is withheld when the server has already told us it can only
                400 (a referee row whose match has left `Scheduled`). `offersNegativeDecision` carries
                the condition and its fail-open rule; the same gate is on the My Assignments card so
                the two surfaces agree. The row keeps Accept — which CO-780 made work on a Completed
                match — and Details, so nothing becomes unreachable. */}
            {offersNegativeDecision(item) && (
            <Button
              variant={negativeVariant}
              size="xs"
              disabled={disabled}
              loading={busy === 'negative'}
              onClick={() => onDecision(item, false)}
              aria-label={`${t('Decline')}: ${title}`}
              data-testid="pa-decline"
            >
              {t('Decline')}
            </Button>
            )}
            <Button
              variant={positiveVariant}
              size="xs"
              disabled={disabled}
              loading={busy === 'positive'}
              onClick={() => onDecision(item, true)}
              aria-label={`${t('Accept')}: ${title}`}
              data-testid="pa-accept"
            >
              {t('Accept')}
            </Button>
          </>
        );
      case 'Complete1':
        return (
          <Button
            variant={positiveVariant}
            size="xs"
            disabled={disabled}
            loading={busy === 'positive'}
            onClick={() => onDecision(item, true)}
            aria-label={`${t(completeActionLabel(item.source))}: ${title}`}
            data-testid="pa-complete"
          >
            {t(completeActionLabel(item.source))}
          </Button>
        );
      case 'OpenOnly':
      default:
        return (
          <Button
            variant="default"
            size="xs"
            disabled={disabled}
            onClick={() => onOpen(item)}
            aria-label={`${t('Open')}: ${title}`}
            data-testid="pa-open"
          >
            {t('Open')}
          </Button>
        );
    }
  })();

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:flex-nowrap ${
        item.blocking ? 'bg-amber-500/5' : ''
      }`}
      data-testid={`pa-row-${item.source}`}
      data-entity-id={item.id}
    >
      {/* Source icon — one quiet treatment; amber only when the item blocks the user. */}
      <div
        className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${
          item.blocking ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-muted text-muted-foreground'
        }`}
        aria-hidden="true"
      >
        {item.blocking ? <AlertCircle size={17} /> : <SourceIcon size={17} />}
      </div>

      {/* What + who + when */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
          {item.blocking && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
              {t('Required')}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {[item.actorName, subtitle, relativeTime(item.submittedOn, locale, t)]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {/* A disabled button shows no tooltip on touch and is skipped by some screen readers, so the
            reason is also visible text (same treatment as ApprovalQueue). */}
        {notMyTurn && (
          <p id={notMyTurnId} className="text-xs text-amber-700 dark:text-amber-400" data-testid="pa-not-my-turn">
            {notMyTurnReason}
          </p>
        )}
      </div>

      {/* Quick actions + the always-available deep dive */}
      <div className="flex flex-none items-center gap-2 ms-auto">
        {actions}
        <Button
          variant={emphasiseReview ? 'default' : 'ghost'}
          size="xs"
          disabled={disabled}
          onClick={() => onDetails(item)}
          aria-haspopup={detailsNavigates ? undefined : 'dialog'}
          aria-label={`${emphasiseReview ? t('pendingActions.review') : t('Details')}: ${title}`}
          data-testid="pa-details"
        >
          {emphasiseReview ? t('pendingActions.review') : t('Details')}
        </Button>
      </div>
    </div>
  );
};

export default PendingActionRow;
